import React from 'react';
import { Play, Edit, Trash2, Share2, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const HighlightCard = ({ highlight, onVideoPress, onEdit, onDelete, isOwnHighlight = false }) => {
  const navigate = useNavigate();

  const handleEdit = () => {
    if (onEdit) {
      onEdit(highlight);
    } else {
      toast.info('Edit highlight functionality coming soon!');
    }
  };

  const handleDelete = async () => {
    if (onDelete) {
      try {
        await onDelete(highlight.id);
        toast.success('Highlight deleted successfully');
      } catch (error) {
        toast.error('Failed to delete highlight');
      }
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/highlight/${highlight.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: highlight.title || 'Hockey Highlight',
          text: highlight.description || 'Check out this amazing hockey highlight!',
          url: shareUrl,
        });
      } catch (err) {
        console.log('Error sharing:', err);
        // Fallback to clipboard
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleViewDetail = () => {
    navigate(`/highlight/${highlight.id}`);
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
      {/* Player Header */}
      <div className="flex items-center mb-3">
        {highlight.user?.avatar ? (
          <img
            src={highlight.user.avatar}
            alt="User"
            className="w-10 h-10 rounded-full mr-3"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center mr-3">
            <span className="text-white font-bold text-sm">
              {highlight.user?.first_name?.[0]}{highlight.user?.last_name?.[0]}
            </span>
          </div>
        )}
        <div className="flex-1">
          <p className="font-medium text-gray-900">
            {highlight.user?.first_name} {highlight.user?.last_name}
          </p>
          <p className="text-sm text-gray-500">
            {new Date(highlight.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Highlight Info */}
      <div className="mb-3">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">{highlight.title}</h3>
        {highlight.description && (
          <p className="text-sm text-gray-600">{highlight.description}</p>
        )}
      </div>

      {/* Media Content */}
      <div className="mb-3">
        <button
          onClick={() => onVideoPress && onVideoPress(highlight)}
          className="w-full relative group max-w-[300px]"
        >
          {highlight.media_url ? (
            <div className="relative aspect-video bg-gray-200 rounded-lg overflow-hidden">
              {highlight.media_type === 'video' ? (
                <>
                  <video
                    src={highlight.media_url}
                    className="w-full h-full object-cover"
                    muted
                    preload="metadata"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center group-hover:bg-opacity-40 transition-all">
                    <Play className="text-white" size={48} />
                    <span className="text-white text-sm ml-2">Tap to play</span>
                  </div>
                </>
              ) : (
                <img
                  src={highlight.media_url}
                  alt="Highlight"
                  className="w-full h-full object-cover"
                />
              )}
            </div>
          ) : (
            <div className="aspect-video bg-gray-200 rounded-lg flex items-center justify-center">
              <Play className="text-gray-400" size={48} />
              <span className="text-gray-500 text-sm ml-2">No media</span>
            </div>
          )}
        </button>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-2">
          <button
            onClick={handleViewDetail}
            className="flex items-center text-gray-600 hover:text-blue-600 transition-colors"
          >
            <Eye size={16} className="mr-1" />
            <span className="text-sm">View</span>
          </button>
          <button
            onClick={handleShare}
            className="flex items-center text-gray-600 hover:text-green-600 transition-colors"
          >
            <Share2 size={16} className="mr-1" />
            <span className="text-sm">Share</span>
          </button>
        </div>
        
        {isOwnHighlight && (
          <div className="flex space-x-2">
            <button
              onClick={handleEdit}
              className="text-gray-400 hover:text-blue-600 p-1"
            >
              <Edit size={16} />
            </button>
            <button
              onClick={handleDelete}
              className="text-gray-400 hover:text-red-600 p-1"
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HighlightCard; 